import React, { useState, useRef, useEffect } from "react";
import SectionHeading from "./sectionHeading";
import ContainerCard from "./containerCard";
import { Button, Input, Select } from "@speedoc/design-assets";
import sendEmail from "../../utils/sendEmail";
import { useMediaQuery } from "react-responsive";
import { trackCustom } from "../../services/analytics";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "react-datepicker/dist/react-datepicker-cssmodules.css";
import useBrowserType from "../../hooks/useBrowserType";
import SubmissionConfirmation from "./submissionConfirmation";
import { GlobalStore } from "../../stores/global";

export default function ContactFormHealthScreening({ content }) {
  const countryCode = GlobalStore.useState(s => s.currentCountryCode);

  const { isFirefox, isChrome } = useBrowserType();
  const isMobile = useMediaQuery({ query: "(max-width: 992px)" });

  const optionsDropDown = [
    { id: "Life", title: "Life" },
    { id: "LifePrime", title: "Life Prime" },
    { id: "LifeMax", title: "Life Max" }
  ];

  const initialState = {
    name: "",
    phone: "",
    email: "",
    address: "",
    date: "",
    package: optionsDropDown[0].id
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formHasErrors, setFormHasErrors] = useState(false);
  const [fieldsMissing, setFieldsMissing] = useState({ name: false, phone: false, date: false });
  const [formHeight, setFormHeight] = useState(undefined);
  const [formData, setFormData] = useState(initialState);
  const [hoursOutOfRange, setHoursOutOfRange] = useState(false);
  const [error, setError] = useState(false);
  const targetRef = useRef();
  const { title, subtitle, emailFrom, emailTo, parent, fields, subject } = content;
  const formInfo = { fields, emailFrom, parent, subject, emailTo };

  const isChromeOrFirefox = () => {
    return isChrome || isFirefox;
  };

  const handleInputChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    if (formData.date) setHoursOutOfRange(formData.date?.getHours() < 8 || formData.date?.getHours() > 15);
  }, [formData]);

  const handleDateChange = e => {
    setFormData({ ...formData, date: e });
  };

  const handleSelectChange = (e, name) => {
    setFormData({ ...formData, [name]: e });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setIsSubmitting(true);
    setFieldsMissing({ name: false, phone: false, date: false });
    if (formData.date !== "" && formData.phone !== "" && formData.name !== "" && !hoursOutOfRange) {
      try {
        await sendEmail(formData, formInfo);
        setFormHeight(targetRef.current.offsetHeight);
      } catch (err) {
        console.log(err);
      } finally {
        trackCustom("Schedule", countryCode, { phone: formData.phone, name: formData.name, date: formData.date });
        setIsSubmitting(false);
        setFormHasErrors(false);
        setError(false);
        setIsSubmitted(true);
        setFormData({ ...initialState });
      }
    } else {
      setError(false);
      setFieldsMissing({ phone: formData.phone === "", date: formData.date === "", name: formData.name === "" });
      setIsSubmitting(false);
      setFormHasErrors(true);
    }
  };

  const handleBackClick = () => {
    setIsSubmitted(false);
  };

  return (
    <div className="contactFormHealthScreening" id="contactFormAnchor">
      {title && <SectionHeading title={title} subtitle={subtitle} />}
      <ContainerCard>
        {!isSubmitted ? (
          <div className="container" ref={targetRef}>
            <div className="text">
              <div className="title">{content.formTitle}</div>
              <div className="description">{content?.description}</div>
            </div>
            <form className="form">
              <Input
                className={`sm ${isChromeOrFirefox() ? "" : "margin-bottom"} ${
                  fieldsMissing.name ? "input-error" : ""
                }`}
                required
                id="name"
                name="name"
                placeholder="Full Name"
                value={formData.name}
                onChange={handleInputChange}
              />
              <Input
                className={`sm ${fieldsMissing.phone ? "input-error" : ""}`}
                required
                id="phone"
                name="phone"
                placeholder="Phone Number"
                value={formData.phone}
                onChange={handleInputChange}
              />
              <Input
                className={`sm ${fieldsMissing.email ? "input-error" : ""}`}
                required
                id="email"
                name="email"
                placeholder="Email"
                value={formData.email}
                onChange={handleInputChange}
              />
              <Input
                className={`sm ${fieldsMissing.address ? "input-error" : ""}`}
                required
                id="address"
                name="address"
                placeholder="Home Address"
                value={formData.address}
                onChange={handleInputChange}
              />
              <div className="datePickers contactFormDate">
                <div className="dateWrapper">
                  <DatePicker
                    placeholderText="Preferred Date"
                    name="date"
                    selected={formData.date}
                    minDate={new Date(Date.now() + 3 * 86400000)}
                    onChange={handleDateChange}
                    className={`date ${fieldsMissing.date ? "input-error" : ""}`}
                    popperClassName="popper"
                  />
                </div>
                <div className="dateWrapper">
                  <DatePicker
                    placeholderText="Preferred Time"
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={15}
                    timeCaption="Time"
                    dateFormat="h:mm aa"
                    name="time"
                    selected={formData.date}
                    onChange={handleDateChange}
                    className={`time ${fieldsMissing.date ? "input-error" : ""}`}
                  />
                  {hoursOutOfRange ? (
                    <span className="errorMessage hours">Kindly select a timing between 8am & 3pm.</span>
                  ) : null}
                </div>
              </div>
              <Select
                required
                name="package"
                title="Life"
                defaultValue={formData.field || null}
                list={optionsDropDown}
                onChange={v => handleSelectChange(v, "field")}
                className="sm"
              />
              <div className="buttonSection">
                <Button onClick={handleSubmit} disabled={isSubmitting} className="primary sm">
                  {isSubmitting ? "Please wait..." : content.buttonText}
                </Button>
                {formHasErrors ? <p className="errorMessage">Please fill in the required fields.</p> : null}
                {!isMobile && error ? <p className="errorMessage">{error}</p> : null}
              </div>
            </form>
            <div />
          </div>
        ) : (
          <SubmissionConfirmation formHeight={formHeight} handleBackClick={handleBackClick} />
        )}
      </ContainerCard>
    </div>
  );
}
